/**
 * MiloRunbooksPanel — Milo Phase 5B authoring surface.
 *
 * Admin-only. Read/write access to the `admin_kb_docs` collection. This is
 * the grounding foundation for Milo Phase 5C+ (Q&A console). No live Q&A,
 * no action execution.
 *
 * Layout: two-column (list rail + reader/editor pane). Restrained luxury
 * admin styling — white / cool-gray surfaces, gold as accent only, inline
 * CSS, visually consistent with the rest of the AI tab.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ScrollText, Plus, Search, Edit3, Save, X, RotateCcw,
  History, Tag as TagIcon, Lock, ChevronRight, AlertCircle,
} from 'lucide-react';
import { adminAuthHeader } from '../../data/api';

const API = process.env.REACT_APP_BACKEND_URL;
// Evaluated PER-REQUEST so the real admin token (set at login) is read, not a stale module-load value.
const ADMIN_AUTH = () => adminAuthHeader();

const GOLD = '#C6A75A';
const GOLD_DK = '#8E7A2C';
const DARK = '#1B1812';
const MUTED = '#5C5C66';
const BORDER = 'rgba(0,0,0,0.10)';
const BORDER_GOLD = 'rgba(198,167,90,0.32)';
const SURFACE = '#FFFFFF';
const SOFT_BG = 'rgba(0,0,0,0.025)';

const SCOPES = [
  { key: 'all', label: 'All scopes' },
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'guests', label: 'Guests' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'finance', label: 'Finance' },
  { key: 'messaging', label: 'Messaging' },
  { key: 'settings', label: 'Settings' },
  { key: 'ai', label: 'AI' },
  { key: 'cross', label: 'Cross' },
];

const SCOPE_FILL = {
  dashboard: '#1E7344', guests: '#2C66B8', vendors: '#7A4FB6',
  finance: '#9D6F1F',  messaging: '#B23A48', settings: '#5C5C66',
  ai: GOLD_DK,         cross: '#3A4252',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const ScopePill = ({ scope, size = 'sm' }) => {
  const fill = SCOPE_FILL[scope] || GOLD_DK;
  return (
    <span data-testid={`runbook-scope-pill-${scope}`}
      style={{
        fontSize: size === 'sm' ? 9 : 10, fontWeight: 800,
        letterSpacing: '0.10em', textTransform: 'uppercase',
        padding: size === 'sm' ? '2px 7px' : '3px 9px',
        borderRadius: 99, background: `${fill}14`,
        color: fill, border: `1px solid ${fill}38`,
      }}>
      {scope}
    </span>
  );
};

const StatusBadge = ({ children, tone = 'gold' }) => {
  const palette = tone === 'gold'
    ? { bg: 'rgba(198,167,90,0.12)', fg: GOLD_DK, br: BORDER_GOLD }
    : tone === 'green'
    ? { bg: 'rgba(30,115,68,0.10)', fg: '#1E7344', br: 'rgba(30,115,68,0.28)' }
    : { bg: 'rgba(0,0,0,0.05)', fg: MUTED, br: 'rgba(0,0,0,0.16)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}`,
      display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {children}
    </span>
  );
};

const BodyMd = ({ text }) => {
  // Lightweight Markdown-ish renderer: headings (## / ###), bold (**...**),
  // bullets (- ...), and paragraphs. No external lib — restrained on purpose.
  const lines = (text || '').split('\n');
  const blocks = [];
  let buf = [];
  const flush = () => {
    if (buf.length === 0) return;
    blocks.push({ kind: 'p', items: buf });
    buf = [];
  };
  for (const ln of lines) {
    const l = ln.trimEnd();
    if (l.startsWith('## ')) { flush(); blocks.push({ kind: 'h2', text: l.slice(3) }); continue; }
    if (l.startsWith('### ')) { flush(); blocks.push({ kind: 'h3', text: l.slice(4) }); continue; }
    if (l.startsWith('- ')) { flush(); blocks.push({ kind: 'li', text: l.slice(2) }); continue; }
    if (l === '') { flush(); continue; }
    buf.push(l);
  }
  flush();

  const renderInline = (s) => {
    // **bold**
    const parts = String(s).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return <strong key={i} style={{ color: DARK, fontWeight: 700 }}>{p.slice(2, -2)}</strong>;
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div data-testid="runbook-body-rendered"
      style={{ fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 13, lineHeight: 1.7, color: DARK }}>
      {blocks.map((b, i) => {
        if (b.kind === 'h2') return (
          <h3 key={i} style={{ margin: '14px 0 6px',
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 18, fontWeight: 700, color: DARK,
            letterSpacing: '0.2px' }}>{renderInline(b.text)}</h3>
        );
        if (b.kind === 'h3') return (
          <h4 key={i} style={{ margin: '12px 0 4px', fontSize: 13,
            fontWeight: 700, color: DARK, letterSpacing: '0.2px' }}>
            {renderInline(b.text)}
          </h4>
        );
        if (b.kind === 'li') return (
          <div key={i} style={{ display: 'flex', gap: 8,
            marginBottom: 4, color: DARK }}>
            <span style={{ color: GOLD_DK, fontWeight: 700, marginTop: 1 }}>•</span>
            <span style={{ flex: 1 }}>{renderInline(b.text)}</span>
          </div>
        );
        return (
          <p key={i} style={{ margin: '0 0 10px', color: DARK }}>
            {b.items.map((ln, j) => (
              <React.Fragment key={j}>
                {renderInline(ln)}
                {j < b.items.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

const Editor = ({ initial, onSave, onCancel, saving }) => {
  const [scope, setScope] = useState(initial?.scope || 'dashboard');
  const [title, setTitle] = useState(initial?.title || '');
  const [bodyMd, setBodyMd] = useState(initial?.body_md || '');
  const [tagsRaw, setTagsRaw] = useState((initial?.tags || []).join(', '));
  const [error, setError] = useState(null);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!scope) { setError('Scope is required.'); return; }
    setError(null);
    onSave({
      scope: scope.toLowerCase(),
      title: title.trim(),
      body_md: bodyMd,
      tags: tagsRaw.split(',').map(t => t.trim()).filter(Boolean),
    });
  };

  return (
    <form data-testid="runbook-editor" onSubmit={submit}
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
        <div>
          <label style={{ display: 'block', fontSize: 9, fontWeight: 800,
            letterSpacing: '0.10em', textTransform: 'uppercase', color: MUTED,
            marginBottom: 5 }}>Scope</label>
          <select data-testid="runbook-editor-scope" value={scope}
            onChange={e => setScope(e.target.value)}
            style={{ width: '100%', padding: '9px 10px', borderRadius: 8,
              border: `1px solid ${BORDER}`, background: '#FFFFFF',
              fontSize: 12, fontWeight: 600, color: DARK, outline: 'none',
              cursor: 'pointer' }}>
            {SCOPES.filter(s => s.key !== 'all').map(s =>
              <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 9, fontWeight: 800,
            letterSpacing: '0.10em', textTransform: 'uppercase', color: MUTED,
            marginBottom: 5 }}>Title</label>
          <input data-testid="runbook-editor-title"
            value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Dashboard — Operator overview"
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8,
              border: `1px solid ${BORDER}`, background: '#FFFFFF',
              fontSize: 13, color: DARK, outline: 'none' }} />
        </div>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: 9, fontWeight: 800,
          letterSpacing: '0.10em', textTransform: 'uppercase', color: MUTED,
          marginBottom: 5 }}>Body (Markdown)</label>
        <textarea data-testid="runbook-editor-body" value={bodyMd}
          onChange={e => setBodyMd(e.target.value)}
          placeholder="## Purpose&#10;Short, restrained overview that Milo can ground against…"
          rows={14}
          style={{ width: '100%', padding: '11px 13px', borderRadius: 8,
            border: `1px solid ${BORDER}`, background: '#FFFFFF',
            fontSize: 13, lineHeight: 1.6, color: DARK, outline: 'none',
            fontFamily: 'ui-monospace, Menlo, monospace', resize: 'vertical' }} />
        <div style={{ fontSize: 10, color: MUTED, marginTop: 4, fontStyle: 'italic' }}>
          Supports <code>##</code> / <code>###</code> headings, <code>- bullets</code>, and <code>**bold**</code>.
        </div>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: 9, fontWeight: 800,
          letterSpacing: '0.10em', textTransform: 'uppercase', color: MUTED,
          marginBottom: 5 }}>Tags</label>
        <input data-testid="runbook-editor-tags" value={tagsRaw}
          onChange={e => setTagsRaw(e.target.value)}
          placeholder="comma, separated, tags"
          style={{ width: '100%', padding: '9px 12px', borderRadius: 8,
            border: `1px solid ${BORDER}`, background: '#FFFFFF',
            fontSize: 12, color: DARK, outline: 'none' }} />
      </div>

      {error && (
        <div data-testid="runbook-editor-error"
          style={{ padding: '8px 12px', borderRadius: 8,
            background: 'rgba(178,58,72,0.06)',
            border: '1px solid rgba(178,58,72,0.22)',
            fontSize: 12, color: '#B23A48',
            display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={13} /> {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button type="submit" data-testid="runbook-editor-save"
          disabled={saving}
          style={{ padding: '10px 18px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
            color: '#1B1812', fontWeight: 800, fontSize: 12,
            letterSpacing: '0.6px', textTransform: 'uppercase',
            cursor: saving ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: '0 2px 10px rgba(198,167,90,0.30)' }}>
          <Save size={13} /> {saving ? 'Saving…' : 'Save runbook'}
        </button>
        <button type="button" data-testid="runbook-editor-cancel"
          onClick={onCancel}
          style={{ padding: '10px 18px', borderRadius: 10,
            border: `1px solid ${BORDER}`, background: '#FFFFFF',
            color: MUTED, fontWeight: 700, fontSize: 12,
            letterSpacing: '0.6px', textTransform: 'uppercase',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <X size={13} /> Cancel
        </button>
      </div>
    </form>
  );
};

const VersionDrawer = ({ familyId, currentVersionId, onClose, onRestored }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/runbooks/${familyId}/versions`,
        { headers: ADMIN_AUTH() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    }
    setLoading(false);
  }, [familyId]);

  useEffect(() => { load(); }, [load]);

  const restore = async (versionId) => {
    setRestoringId(versionId);
    try {
      const r = await fetch(
        `${API}/api/runbooks/${familyId}/restore/${versionId}`,
        {
          method: 'POST',
          headers: { ...ADMIN_AUTH(), 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const fresh = await r.json();
      onRestored(fresh);
    } catch (e) {
      setError(String(e.message || e));
    }
    setRestoringId(null);
  };

  return (
    <div data-testid="runbook-version-drawer"
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 460,
        background: '#FFFFFF', borderLeft: `1px solid ${BORDER}`,
        boxShadow: '-12px 0 32px rgba(20,15,5,0.10)',
        zIndex: 200, display: 'flex', flexDirection: 'column',
      }}>
      <div style={{ padding: '18px 20px', borderBottom: `1px solid ${BORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 18, fontWeight: 700, color: DARK }}>
            Version history
          </h3>
          <span style={{ fontSize: 11, color: MUTED }}>
            Older versions are preserved. Restore creates a new version.
          </span>
        </div>
        <button data-testid="runbook-version-drawer-close" onClick={onClose}
          style={{ background: 'transparent', border: `1px solid ${BORDER}`,
            borderRadius: 8, padding: 6, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center' }}>
          <X size={14} color={MUTED} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
        {loading && <div style={{ color: MUTED, fontSize: 12, fontStyle: 'italic',
          padding: '20px 0' }}>Loading versions…</div>}
        {error && (
          <div data-testid="runbook-version-error"
            style={{ padding: '8px 12px', borderRadius: 8,
              background: 'rgba(178,58,72,0.06)',
              border: '1px solid rgba(178,58,72,0.22)',
              fontSize: 12, color: '#B23A48' }}>{error}</div>
        )}
        {!loading && !error && data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.versions.map(v => {
              const isCurrent = v.id === currentVersionId;
              return (
                <div key={v.id}
                  data-testid={`runbook-version-row-v${v.version}`}
                  style={{
                    border: isCurrent ? `1.5px solid ${GOLD}` : `1px solid ${BORDER}`,
                    borderRadius: 10, padding: '12px 14px',
                    background: isCurrent ? 'rgba(198,167,90,0.06)' : '#FFFFFF',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
                      fontSize: 16, fontWeight: 700, color: DARK }}>
                      v{v.version}
                    </span>
                    {isCurrent && <StatusBadge tone="green">Current</StatusBadge>}
                    {v.restored_from_version_number && (
                      <StatusBadge tone="muted">
                        Restored from v{v.restored_from_version_number}
                      </StatusBadge>
                    )}
                    <span style={{ fontSize: 11, color: MUTED, marginLeft: 'auto' }}>
                      {fmtDate(v.updated_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: DARK, lineHeight: 1.5,
                    whiteSpace: 'nowrap', overflow: 'hidden',
                    textOverflow: 'ellipsis' }} title={v.body_md}>
                    {v.body_md
                      ? v.body_md.replace(/\s+/g, ' ').slice(0, 140)
                      : <span style={{ color: MUTED, fontStyle: 'italic' }}>(empty body)</span>}
                  </div>
                  <div style={{ fontSize: 10, color: MUTED }}>
                    by {v.updated_by || v.created_by || 'admin'}
                  </div>
                  {!isCurrent && (
                    <button data-testid={`runbook-version-restore-v${v.version}`}
                      onClick={() => restore(v.id)}
                      disabled={restoringId === v.id}
                      style={{ alignSelf: 'flex-start', marginTop: 2,
                        padding: '6px 12px', borderRadius: 8,
                        border: `1px solid ${BORDER_GOLD}`,
                        background: '#FFFFFF', color: GOLD_DK,
                        fontWeight: 700, fontSize: 11,
                        letterSpacing: '0.4px', textTransform: 'uppercase',
                        cursor: restoringId === v.id ? 'wait' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <RotateCcw size={11} />
                      {restoringId === v.id ? 'Restoring…' : 'Restore as latest'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const MiloRunbooksPanel = () => {
  const [runbooks, setRunbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scopeFilter, setScopeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [mode, setMode] = useState('view'); // view | edit | create
  const [saving, setSaving] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/runbooks`, { headers: ADMIN_AUTH() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRunbooks(d.runbooks || []);
    } catch (e) {
      setError(String(e.message || e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Phase 5C — honor a one-shot deep-link hint set by the Milo Console
  // when the operator clicks a citation chip. Lands the rail on the
  // exact runbook that grounded the answer. Consumed and cleared on
  // first read; subsequent visits behave normally (auto-pick first).
  useEffect(() => {
    try {
      const want = sessionStorage.getItem('milo_runbook_open_family_id');
      if (want) {
        sessionStorage.removeItem('milo_runbook_open_family_id');
        setSelectedFamilyId(want);
        setMode('view');
      }
    } catch (_) {}
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return runbooks.filter(r => {
      if (scopeFilter !== 'all' && r.scope !== scopeFilter) return false;
      if (!q) return true;
      const hay = [
        r.title || '',
        r.body_md || '',
        ...(r.tags || []),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [runbooks, scopeFilter, search]);

  const selected = useMemo(
    () => runbooks.find(r => r.family_id === selectedFamilyId) || null,
    [runbooks, selectedFamilyId],
  );

  // Auto-pick the first runbook when nothing is selected and the list
  // resolves. Avoids a stale empty pane on first paint.
  useEffect(() => {
    if (!selectedFamilyId && filtered.length > 0 && mode === 'view') {
      setSelectedFamilyId(filtered[0].family_id);
    }
  }, [filtered, selectedFamilyId, mode]);

  const startCreate = () => {
    setMode('create');
    setSelectedFamilyId(null);
  };

  const startEdit = () => setMode('edit');
  const cancelEdit = () => setMode('view');

  const onSaveCreate = async (payload) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/runbooks`, {
        method: 'POST',
        headers: { ...ADMIN_AUTH(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const fresh = await r.json();
      await fetchAll();
      setSelectedFamilyId(fresh.family_id);
      setMode('view');
    } catch (e) {
      setError(String(e.message || e));
    }
    setSaving(false);
  };

  const onSaveEdit = async (payload) => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/runbooks/${selected.family_id}`, {
        method: 'PUT',
        headers: { ...ADMIN_AUTH(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
      setMode('view');
    } catch (e) {
      setError(String(e.message || e));
    }
    setSaving(false);
  };

  const onRestored = async (_freshDoc) => {
    await fetchAll();
    setVersionsOpen(false);
  };

  return (
    <div data-testid="milo-subtab-panel-runbooks"
      style={{ display: 'flex', flexDirection: 'column', gap: 14,
        border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '20px 22px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12,
          minWidth: 0 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10,
            background: 'rgba(198,167,90,0.10)',
            border: `1px solid ${BORDER_GOLD}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0 }}>
            <ScrollText size={20} color={GOLD_DK} strokeWidth={1.6} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8,
              flexWrap: 'wrap', marginBottom: 4 }}>
              <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 22, fontWeight: 700, color: DARK,
                letterSpacing: '0.2px' }}>
                Runbooks
              </h3>
              <StatusBadge tone="gold">Phase 5B · Live</StatusBadge>
              <StatusBadge tone="muted">Grounding · read by Milo</StatusBadge>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: MUTED,
              lineHeight: 1.55, maxWidth: 720 }}>
              Structured admin documentation that grounds the Milo assistant.
              Operator-curated, versioned. Edits create a new version — prior
              versions are preserved and restorable. Milo Q&A activates in a
              later phase and will read from this exact corpus.
            </p>
          </div>
        </div>
        <button data-testid="runbooks-new-btn" onClick={startCreate}
          style={{ padding: '10px 18px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
            color: '#1B1812', fontWeight: 800, fontSize: 12,
            letterSpacing: '0.6px', textTransform: 'uppercase',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: '0 2px 10px rgba(198,167,90,0.30)' }}>
          <Plus size={14} /> New Runbook
        </button>
      </div>

      {error && (
        <div data-testid="runbooks-error"
          style={{ padding: '8px 12px', borderRadius: 8,
            background: 'rgba(178,58,72,0.06)',
            border: '1px solid rgba(178,58,72,0.22)',
            fontSize: 12, color: '#B23A48',
            display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {/* Two-column shell */}
      <div style={{ display: 'grid',
        gridTemplateColumns: '320px 1fr', gap: 14,
        minHeight: 500 }}>

        {/* Left rail */}
        <div data-testid="runbooks-rail"
          style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
            background: '#FFFFFF', padding: '14px 12px',
            display: 'flex', flexDirection: 'column', gap: 12,
            maxHeight: 720, overflowY: 'auto' }}>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={13} color={MUTED}
              style={{ position: 'absolute', left: 10, top: '50%',
                transform: 'translateY(-50%)' }} />
            <input data-testid="runbooks-search"
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search title, body, tags…"
              style={{ width: '100%', padding: '8px 10px 8px 30px',
                borderRadius: 8, border: `1px solid ${BORDER}`,
                background: '#FFFFFF', fontSize: 12, color: DARK,
                outline: 'none' }} />
          </div>

          {/* Scope chips */}
          <div data-testid="runbooks-scope-filter"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {SCOPES.map(s => {
              const active = scopeFilter === s.key;
              return (
                <button key={s.key}
                  data-testid={`runbooks-scope-${s.key}`}
                  onClick={() => setScopeFilter(s.key)}
                  style={{ padding: '5px 9px', borderRadius: 99,
                    border: `1px solid ${active ? GOLD : BORDER}`,
                    background: active ? 'rgba(198,167,90,0.10)' : '#FFFFFF',
                    color: active ? GOLD_DK : MUTED,
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                    textTransform: 'uppercase', cursor: 'pointer',
                    transition: 'all 0.15s ease' }}>
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* List */}
          {loading ? (
            <div style={{ color: MUTED, fontSize: 12, fontStyle: 'italic',
              padding: '20px 0', textAlign: 'center' }}>
              Loading runbooks…
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="runbooks-empty-rail"
              style={{ padding: '24px 14px', textAlign: 'center',
                border: `1px dashed ${BORDER}`, borderRadius: 10,
                color: MUTED }}>
              <ScrollText size={20} color={GOLD} strokeWidth={1.4}
                style={{ marginBottom: 6 }} />
              <div style={{ fontSize: 12, fontWeight: 600, color: DARK,
                marginBottom: 3 }}>
                {runbooks.length === 0 ? 'No runbooks yet' : 'No runbooks match'}
              </div>
              <div style={{ fontSize: 11, lineHeight: 1.55 }}>
                {runbooks.length === 0
                  ? 'Create the first runbook to ground Milo.'
                  : 'Try clearing search or switching scope.'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {filtered.map(r => {
                const active = r.family_id === selectedFamilyId;
                return (
                  <button key={r.family_id}
                    data-testid={`runbooks-row-${r.scope}`}
                    onClick={() => { setSelectedFamilyId(r.family_id); setMode('view'); }}
                    style={{
                      textAlign: 'left',
                      padding: '10px 11px', borderRadius: 10,
                      border: `1px solid ${active ? GOLD : 'transparent'}`,
                      background: active ? 'rgba(198,167,90,0.06)' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', gap: 5,
                      transition: 'all 0.15s ease',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center',
                      gap: 6, flexWrap: 'wrap' }}>
                      <ScopePill scope={r.scope} />
                      <span style={{ fontSize: 9, fontWeight: 700,
                        color: MUTED, letterSpacing: '0.04em',
                        textTransform: 'uppercase' }}>
                        v{r.version}
                      </span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600,
                      color: DARK, lineHeight: 1.35,
                      whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis' }} title={r.title}>
                      {r.title}
                    </span>
                    <span style={{ fontSize: 10, color: MUTED }}>
                      Updated {fmtDate(r.updated_at)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right pane */}
        <div data-testid="runbooks-pane"
          style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
            background: '#FFFFFF', padding: '20px 22px',
            display: 'flex', flexDirection: 'column', gap: 14 }}>

          {mode === 'create' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Plus size={16} color={GOLD_DK} />
                <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                  fontSize: 20, fontWeight: 700, color: DARK }}>
                  New runbook
                </h3>
              </div>
              <Editor onSave={onSaveCreate} onCancel={() => setMode('view')}
                saving={saving} />
            </>
          )}

          {mode !== 'create' && !selected && !loading && (
            <div data-testid="runbooks-pane-empty"
              style={{ padding: '60px 24px', textAlign: 'center',
                color: MUTED, display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 10 }}>
              <ScrollText size={32} color={GOLD} strokeWidth={1.3} />
              <div style={{ fontSize: 14, fontWeight: 600, color: DARK }}>
                Select a runbook
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.6, maxWidth: 380 }}>
                Pick one from the rail to view its body, edit, or browse
                version history.
              </div>
            </div>
          )}

          {mode === 'view' && selected && (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start',
                justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center',
                    gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <ScopePill scope={selected.scope} size="md" />
                    <StatusBadge tone="muted">v{selected.version}</StatusBadge>
                  </div>
                  <h3 data-testid="runbook-view-title"
                    style={{ margin: 0,
                      fontFamily: 'Cormorant Garamond, Georgia, serif',
                      fontSize: 22, fontWeight: 700, color: DARK,
                      letterSpacing: '0.2px' }}>
                    {selected.title}
                  </h3>
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                    Updated {fmtDate(selected.updated_at)} by {selected.updated_by || 'admin'}
                    {' · '}
                    Created {fmtDate(selected.created_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button data-testid="runbook-view-edit-btn" onClick={startEdit}
                    style={{ padding: '8px 14px', borderRadius: 9,
                      border: `1px solid ${BORDER_GOLD}`,
                      background: '#FFFFFF', color: GOLD_DK,
                      fontWeight: 700, fontSize: 11,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Edit3 size={12} /> Edit
                  </button>
                  <button data-testid="runbook-view-versions-btn"
                    onClick={() => setVersionsOpen(true)}
                    style={{ padding: '8px 14px', borderRadius: 9,
                      border: `1px solid ${BORDER}`,
                      background: '#FFFFFF', color: MUTED,
                      fontWeight: 700, fontSize: 11,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <History size={12} /> Versions
                  </button>
                </div>
              </div>

              {(selected.tags || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {selected.tags.map(t => (
                    <span key={t} data-testid={`runbook-view-tag-${t}`}
                      style={{ display: 'inline-flex', alignItems: 'center',
                        gap: 4, fontSize: 10, color: MUTED,
                        padding: '3px 8px', borderRadius: 99,
                        background: SOFT_BG,
                        border: `1px solid ${BORDER}` }}>
                      <TagIcon size={9} /> {t}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 14 }}>
                {selected.body_md
                  ? <BodyMd text={selected.body_md} />
                  : <div style={{ fontSize: 12, color: MUTED, fontStyle: 'italic' }}>
                      (Empty body — click Edit to add content.)
                    </div>}
              </div>
            </>
          )}

          {mode === 'edit' && selected && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Edit3 size={15} color={GOLD_DK} />
                <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                  fontSize: 20, fontWeight: 700, color: DARK }}>
                  Edit runbook
                </h3>
                <span style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>
                  Saving creates a new version (current: v{selected.version}).
                </span>
              </div>
              <Editor initial={selected} onSave={onSaveEdit}
                onCancel={cancelEdit} saving={saving} />
            </>
          )}
        </div>
      </div>

      {versionsOpen && selected && (
        <VersionDrawer
          familyId={selected.family_id}
          currentVersionId={selected.id}
          onClose={() => setVersionsOpen(false)}
          onRestored={onRestored}
        />
      )}
    </div>
  );
};

export default MiloRunbooksPanel;
